interface EndpointSectionItem {
  label: string;
  value: string;
  tone?: 'success' | 'warning' | 'ghost' | string;
}

export interface EndpointSection {
  title: string;
  items: EndpointSectionItem[];
}

export interface EndpointAction {
  label: string;
  href: string;
  disabled: boolean;
}

export interface EndpointRecord {
  id: string;
  endpointKey: string;
  version: number;
  siteId: string;
  macAddress: string;
  ipAddress: string;
  switchDeviceId: string;
  switchPort: string;
  vlan: string;
  arpDeviceId: string;
  sourceMacSnapshotId: string;
  sourceArpSnapshotId: string;
  firstSeenAt: string;
  lastSeenAt: string;
  updatedAt: string;
  attrs: Record<string, unknown> | null;
  sections: EndpointSection[];
  actions: EndpointAction[];
}

export interface EndpointListResponse {
  items: EndpointRecord[];
  next?: string | null;
  prev?: string | null;
  exactTotal?: number | null;
}

const readString = (value: unknown): string => (typeof value === 'string' ? value.trim() : value == null ? '' : String(value).trim());

const readNumber = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const parsed = Number(readString(value));
  return Number.isFinite(parsed) ? parsed : 0;
};

const readObject = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

const readEndpointSections = (value: unknown): EndpointSection[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((section) => {
      const raw = readObject(section);
      const items = Array.isArray(raw?.items)
        ? raw.items
            .map((item) => {
              const rawItem = readObject(item);
              const label = readString(rawItem?.label);
              const itemValue = readString(rawItem?.value);
              if (!label || !itemValue) {
                return null;
              }
              return { label, value: itemValue, tone: readString(rawItem?.tone) };
            })
            .filter(Boolean) as EndpointSectionItem[]
        : [];
      const title = readString(raw?.title);
      if (!title || items.length === 0) {
        return null;
      }
      return { title, items };
    })
    .filter(Boolean) as EndpointSection[];
};

const readEndpointActions = (value: unknown): EndpointAction[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((action) => {
      const raw = readObject(action);
      const label = readString(raw?.label);
      if (!label) {
        return null;
      }
      return {
        label,
        href: readString(raw?.href),
        disabled: Boolean(raw?.disabled)
      };
    })
    .filter(Boolean) as EndpointAction[];
};

const formatBadgeClass = (tone: 'success' | 'warning' | 'ghost') => `ui-badge ui-badge--${tone}`;

export const formatMacAddress = (value: string): string => {
  const compact = value.replace(/[^0-9a-fA-F]/g, '').toLowerCase();
  if (compact.length !== 12) {
    return value || '—';
  }
  return compact.match(/.{1,2}/g)?.join(':') ?? value;
};

export const normalizeEndpointRecord = (raw: unknown): EndpointRecord | null => {
  const record = readObject(raw);
  if (!record) {
    return null;
  }
  const endpointKey = readString(record.endpointKey ?? record.endpoint_key ?? record.macAddress ?? record.mac_address);
  const macAddress = readString(record.macAddress ?? record.mac_address ?? endpointKey);
  if (!endpointKey || !macAddress) {
    return null;
  }
  return {
    id: readString(record.id ?? endpointKey) || endpointKey,
    endpointKey,
    version: Math.max(1, readNumber(record.version)),
    siteId: readString(record.siteId ?? record.site_id),
    macAddress,
    ipAddress: readString(record.ipAddress ?? record.ip_address),
    switchDeviceId: readString(record.switchDeviceId ?? record.switch_device_id),
    switchPort: readString(record.switchPort ?? record.switch_port),
    vlan: readString(record.vlan),
    arpDeviceId: readString(record.arpDeviceId ?? record.arp_device_id),
    sourceMacSnapshotId: readString(record.sourceMacSnapshotId ?? record.source_mac_snapshot_id),
    sourceArpSnapshotId: readString(record.sourceArpSnapshotId ?? record.source_arp_snapshot_id),
    firstSeenAt: readString(record.firstSeenAt ?? record.first_seen_at),
    lastSeenAt: readString(record.lastSeenAt ?? record.last_seen_at),
    updatedAt: readString(record.updatedAt ?? record.updated_at),
    attrs: readObject(record.attrs),
    sections: readEndpointSections(record.sections),
    actions: readEndpointActions(record.actions)
  };
};

export const normalizeEndpointDetailResponse = (raw: unknown): EndpointRecord | null => {
  const payload = readObject(raw);
  return normalizeEndpointRecord(payload?.item ?? payload?.endpoint ?? raw);
};

export const normalizeEndpointListResponse = (raw: unknown): EndpointListResponse => {
  const payload = readObject(raw);
  const meta = readObject(payload?.meta);
  const items = Array.isArray(payload?.items) ? payload.items.map(normalizeEndpointRecord).filter(Boolean) as EndpointRecord[] : [];
  const page = readObject(payload?.page);
  const cursor = readObject(payload?.cursor);
  const next = readString(payload?.next ?? meta?.next ?? page?.next ?? cursor?.next) || null;
  const prev = readString(payload?.prev ?? meta?.prev ?? page?.prev ?? cursor?.prev) || null;
  const exactTotalValue =
    payload?.exactTotal ??
    payload?.exact_total ??
    payload?.totalCount ??
    payload?.total_count ??
    meta?.exactTotal ??
    meta?.exact_total ??
    meta?.totalCount ??
    meta?.total_count ??
    page?.exactTotal ??
    page?.exact_total ??
    page?.totalCount ??
    page?.total_count;
  const exactTotal = exactTotalValue == null ? null : readNumber(exactTotalValue);
  return { items, next, prev, exactTotal };
};

export const EndpointDetailContent = ({ endpoint }: { endpoint: EndpointRecord }) => (
  <div className="endpoint-detail">
    <div className="endpoint-detail__grid">
      {endpoint.sections.length > 0
        ? endpoint.sections.map((section) => (
            <section key={section.title} className="endpoint-detail__panel">
              <h3>{section.title}</h3>
              <dl className="endpoint-detail__list">
                {section.items.map((item) => (
                  <div key={`${section.title}-${item.label}`}>
                    <dt>{item.label}</dt>
                    <dd>
                      {item.tone ? (
                        <span className={formatBadgeClass(item.tone === 'warning' ? 'warning' : item.tone === 'success' ? 'success' : 'ghost')}>
                          {item.value}
                        </span>
                      ) : (
                        item.value
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          ))
        : null}
    </div>
  </div>
);
